import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import {
  Crown, ChevronRight, Award, TrendingUp, Banknote, MapPin, Building2,
  ShieldCheck, Handshake, Star, ClipboardCheck, Boxes, Zap, Radar, ArrowLeft,
} from "lucide-react";

/**
 * Sovereign Performance Platform™ — Phase XVIII surface for the
 * National Executive Dashboard. STRICTLY ADDITIVE · ZERO REGRESSION.
 * Every card orchestrates existing /phase-xviii/* APIs and deep-links
 * into the operational modules that already own the data.
 */
const GOLD = "#C99A2E";
const PURPLE = "#7B2FBE";
const GREEN = "#6BB88A";
const BLUE = "#4FA3D1";
const TEAL = "#2E7D6B";
const AMBER = "#E4A93C";
const RED = "#E06C6C";

const API = process.env.REACT_APP_BACKEND_URL + "/api";
const auth = () => {
  const t = localStorage.getItem("access_token") || localStorage.getItem("token");
  return t ? { Authorization: `Bearer ${t}` } : {};
};

const Card = ({ title, accent, right, children, testId }) => (
  <div className="rounded-sm border" style={{ borderColor: accent + "40", background: "rgba(11,53,88,0.35)" }} data-testid={testId}>
    <div className="px-3 py-2 border-b flex items-center gap-2" style={{ borderColor: accent + "25" }}>
      <div className="w-1 h-4 rounded-sm" style={{ background: accent }} />
      <div className="font-mono uppercase tracking-[0.22em] text-[10px]" style={{ color: accent }}>{title}</div>
      <div className="ml-auto">{right}</div>
    </div>
    <div className="p-3">{children}</div>
  </div>
);

const Chip = ({ children, color = BLUE, filled }) => (
  <span className="inline-flex items-center rounded-sm px-1.5 py-0.5 font-mono uppercase text-[9.5px] tracking-[0.14em]"
        style={{ background: filled ? color : color + "20", color: filled ? "#071B2B" : color, border: "1px solid " + color + "45" }}>
    {children}
  </span>
);

const KpiCard = ({ label, value, unit, accent, to, testId, icon: Icon, sub }) => {
  const inner = (
    <div className="rounded-sm border px-2.5 py-2 h-full transition-colors hover:brightness-125"
         style={{ borderColor: accent + "40", background: "rgba(7,27,43,0.55)" }}>
      <div className="flex items-center gap-1.5 mb-0.5">
        {Icon && <Icon size={11} style={{ color: accent }} />}
        <div className="font-mono uppercase tracking-[0.16em] text-[9.5px] text-[#7C93A5] flex-1 truncate">{label}</div>
        {to && <ChevronRight size={10} className="text-[#7C93A5]" />}
      </div>
      <div className="font-display font-bold text-[15px]" style={{ color: accent }}>
        {unit === "USD" ? `$${Number(value || 0).toLocaleString()}` : (typeof value === "number" ? value.toLocaleString() : (value ?? "—"))}
      </div>
      {sub && <div className="text-[10px] text-[#B3C2CC] truncate">{sub}</div>}
    </div>
  );
  return to ? <Link to={to} data-testid={testId}>{inner}</Link> : <div data-testid={testId}>{inner}</div>;
};

const Bar = ({ value, accent }) => (
  <div className="w-full h-1.5 rounded-sm" style={{ background: "rgba(255,255,255,0.08)" }}>
    <div className="h-1.5 rounded-sm" style={{ width: Math.max(0, Math.min(100, value)) + "%", background: accent }} />
  </div>
);

const StatusDot = ({ v }) => {
  const c = v >= 80 ? GREEN : v >= 65 ? AMBER : RED;
  return <span className="inline-block w-2 h-2 rounded-full" style={{ background: c }} />;
};

const medalColor = (m) => ({ gold: GOLD, silver: "#B0B7BE", bronze: "#B87333" }[m] || AMBER);

export const SovereignPerformancePlatform = () => {
  const [wall, setWall] = useState(null);
  const [pdu, setPdu] = useState(null);
  const [league, setLeague] = useState([]);
  const [provs, setProvs] = useState([]);
  const [delivery, setDelivery] = useState([]);
  const [benefits, setBenefits] = useState(null);
  const [tracker, setTracker] = useState([]);
  const [impl, setImpl] = useState([]);
  const [invest, setInvest] = useState([]);
  const [twin, setTwin] = useState([]);
  const [partners, setPartners] = useState([]);
  const [opps, setOpps] = useState([]);
  const [gov, setGov] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const paths = [
          "phase-xviii/performance-wall","phase-xviii/presidential-delivery-unit",
          "phase-xviii/league-table","phase-xviii/provincial-index",
          "phase-xviii/delivery-centre","phase-xviii/benefits-realisation",
          "phase-xviii/cabinet-action-tracker","phase-xviii/implementation-map",
          "phase-xviii/investment-delivery","phase-xviii/digital-twin/overlays",
          "phase-xviii/development-partner-performance","phase-xviii/strategic-opportunities",
          "phase-xviii/governance-index",
        ];
        const r = await Promise.all(paths.map((p) => axios.get(`${API}/cadastre360/${p}`, { headers: auth() })));
        setWall(r[0].data); setPdu(r[1].data);
        setLeague(r[2].data.table || []); setProvs(r[3].data.provinces || []);
        setDelivery(r[4].data.programmes || []); setBenefits(r[5].data);
        setTracker(r[6].data.items || []); setImpl(r[7].data.provinces || []);
        setInvest(r[8].data.categories || []); setTwin(r[9].data.overlays || []);
        setPartners(r[10].data.partners || []); setOpps(r[11].data.opportunities || []);
        setGov(r[12].data);
      } catch (e) {
        toast.error("Sovereign Performance load failed — " + (e.response?.data?.detail || e.message));
      }
    })();
  }, []);

  const heroKpis = useMemo(() => {
    if (!wall || !pdu) return [];
    return [
      { l: "Programme Delivery",  v: wall.programme_delivery + "%",       a: GOLD,   i: Award,       to: "/app/executive-canvas" },
      { l: "Budget Utilisation",  v: pdu.budget_utilisation_pct + "%",     a: PURPLE, i: Banknote,    to: "/app/revenue" },
      { l: "Investment Attracted",v: pdu.investment_attracted_usd,          a: GREEN,  i: Handshake,   to: "/app/executive-canvas", unit: "USD" },
      { l: "Revenue Generated",   v: pdu.revenue_generated_usd,             a: GOLD,   i: Banknote,    to: "/app/revenue", unit: "USD" },
      { l: "Cabinet Overdue",     v: pdu.cabinet_actions_overdue,           a: RED,    i: ClipboardCheck, to: "/app/cabinet-execution" },
      { l: "National Health",     v: wall.national_health.label,            a: AMBER,  i: Radar,        to: "/app/national-executive-dashboard" },
    ];
  }, [wall, pdu]);

  return (
    <div className="p-6 space-y-4" data-testid="page-sovereign-performance-platform">
      {/* Hero */}
      <div className="rounded-sm border p-6 flex items-start gap-4"
           style={{ borderColor: PURPLE + "60",
                     background: `linear-gradient(120deg, rgba(7,27,43,0.95) 0%, rgba(11,53,88,0.85) 45%, ${PURPLE}30 100%)` }}>
        <Crown size={38} className="mt-1" style={{ color: PURPLE }} />
        <div className="flex-1">
          <div className="font-mono uppercase tracking-[0.32em] text-[10px]" style={{ color: PURPLE }}>
            Cadastre360™ · National Governance Delivery · Sovereign Performance
          </div>
          <h1 className="font-display text-3xl md:text-4xl font-bold text-[#F6F8FA] mt-1">
            Sovereign Performance Platform™
          </h1>
          <div className="text-[13px] text-[#DCE3E8] mt-2 max-w-4xl leading-relaxed">
            Presidential Delivery Unit · Cabinet Delivery Scorecards · Ministry League Table ·
            Provincial Performance · Investment Delivery · National Governance Index. Reuses
            every AI, GIS and PKI service already deployed — no duplication, no regression.
          </div>
        </div>
        <div className="flex flex-col items-end gap-2">
          <Link to="/app/national-executive-dashboard" data-testid="btn-back-ned"
                className="rounded-sm border px-3 py-2 text-[12px] font-medium flex items-center gap-1.5"
                style={{ borderColor: GOLD + "70", color: "#071B2B", background: GOLD }}>
            <ArrowLeft size={13} /> Executive Dashboard
          </Link>
          {gov && (
            <div className="rounded-sm border px-2 py-1 flex items-center gap-2"
                 style={{ borderColor: PURPLE + "60", background: "rgba(7,27,43,0.55)" }}>
              <ShieldCheck size={13} style={{ color: PURPLE }} />
              <div className="font-mono uppercase text-[9.5px] tracking-[0.14em]" style={{ color: PURPLE }}>
                National Governance Index · {gov.overall}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Hero KPIs */}
      <Card title="Presidential Delivery Unit™ · headline" accent={GOLD} testId="spp-headline">
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-2">
          {heroKpis.map((k, i) => (
            <KpiCard key={i} label={k.l} value={k.v} unit={k.unit} accent={k.a} icon={k.i}
                      to={k.to} testId={`spp-kpi-${i}`} />
          ))}
        </div>
      </Card>

      {/* Ministry League Table + Provincial Performance */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
        <Card title="Ministry League Table™ · Cabinet Delivery Scorecards" accent={PURPLE} testId="spp-league"
              right={<Link to="/app/cabinet-workspaces" className="text-[10.5px] font-mono text-[#7C93A5] hover:text-[#F6F8FA]">Workspaces →</Link>}>
          <div className="space-y-1 max-h-[380px] overflow-auto">
            {league.map((row) => (
              <Link key={row.ministry} to="/app/cabinet-workspaces"
                    className="rounded-sm border px-2 py-1.5 flex items-center gap-2 hover:brightness-125"
                    style={{ borderColor: PURPLE + "30", background: "rgba(7,27,43,0.5)" }}
                    data-testid={`spp-min-${row.ministry.toLowerCase().replace(/\s+/g,'-')}`}>
                <div className="w-6 text-center font-mono text-[11px] text-[#7C93A5]">#{row.rank}</div>
                <StatusDot v={row.score} />
                <div className="flex-1 min-w-0">
                  <div className="text-[11.5px] text-[#F6F8FA] truncate">{row.ministry}</div>
                  <div className="text-[9.5px] text-[#7C93A5]">
                    Completion {row.programme_completion}% · Budget {row.budget_utilisation}% · Permits {row.permit_turnaround_days}d
                  </div>
                </div>
                <Chip color={medalColor(row.medal)} filled>{row.medal}</Chip>
                <span className="font-mono text-[11px] text-[#F6F8FA] w-8 text-right">{row.score}</span>
              </Link>
            ))}
          </div>
        </Card>

        <Card title="Provincial Performance Index™" accent={GREEN} testId="spp-provinces"
              right={<Link to="/app/province360" className="text-[10.5px] font-mono text-[#7C93A5] hover:text-[#F6F8FA]">Province360 →</Link>}>
          <div className="space-y-1 max-h-[380px] overflow-auto">
            {provs.map((p) => (
              <Link key={p.province} to="/app/province360"
                    className="rounded-sm border px-2 py-1.5 hover:brightness-125"
                    style={{ borderColor: GREEN + "30", background: "rgba(7,27,43,0.5)" }}
                    data-testid={`spp-prov-${p.province.toLowerCase().replace(/\s+/g,'-')}`}>
                <div className="flex items-center gap-2 mb-1">
                  <div className="w-6 text-center font-mono text-[11px] text-[#7C93A5]">#{p.national_ranking}</div>
                  <StatusDot v={p.score} />
                  <div className="flex-1 text-[11.5px] text-[#F6F8FA]">{p.province}</div>
                  <span className="font-mono text-[11px] text-[#F6F8FA]">{p.score}</span>
                </div>
                <Bar value={p.score} accent={GREEN} />
                <div className="mt-1 text-[9.5px] text-[#7C93A5]">
                  Invest {p.investment} · Mining {p.mining} · Land {p.land_administration} · Env {p.environment}
                </div>
              </Link>
            ))}
          </div>
        </Card>
      </div>

      {/* Executive Implementation Map (deep-link tiles) + Investment Delivery */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
        <Card title="Executive Implementation Map™" accent={BLUE} testId="spp-impl-map"
              right={<Link to="/app/twin" className="text-[10.5px] font-mono text-[#7C93A5] hover:text-[#F6F8FA]">National Digital Twin →</Link>}>
          <div className="grid grid-cols-2 gap-1.5 max-h-[300px] overflow-auto">
            {impl.map((p) => (
              <Link key={p.province} to="/app/province360"
                    className="rounded-sm border px-2 py-1.5 hover:brightness-125"
                    style={{ borderColor: BLUE + "30", background: "rgba(7,27,43,0.5)" }}
                    data-testid={`spp-impl-${p.province.toLowerCase().replace(/\s+/g,'-')}`}>
                <div className="flex items-center gap-1.5 mb-0.5">
                  <StatusDot v={p.colour_score} />
                  <div className="text-[11px] text-[#F6F8FA] flex-1 truncate">{p.province}</div>
                  <span className="font-mono text-[10.5px] text-[#F6F8FA]">{p.colour_score}</span>
                </div>
                <div className="text-[9.5px] text-[#7C93A5]">{p.projects} projects · ${(p.budget_usd/1_000_000).toFixed(0)}M</div>
              </Link>
            ))}
          </div>
        </Card>

        <Card title="National Investment Delivery™" accent={GOLD} testId="spp-invest"
              right={<Link to="/app/executive-canvas" className="text-[10.5px] font-mono text-[#7C93A5] hover:text-[#F6F8FA]">Investment Canvas →</Link>}>
          <div className="space-y-1 max-h-[300px] overflow-auto">
            {invest.map((c) => (
              <div key={c.category} className="rounded-sm border px-2 py-1.5"
                   style={{ borderColor: GOLD + "30", background: "rgba(7,27,43,0.5)" }}
                   data-testid={`spp-invest-${c.category.toLowerCase()}`}>
                <div className="flex items-center gap-2">
                  <div className="text-[11.5px] text-[#F6F8FA] flex-1">{c.category}</div>
                  <Chip color={c.status === "on_track" ? GREEN : c.status === "at_risk" ? AMBER : c.status === "delayed" ? RED : BLUE} filled>{c.status.replace("_"," ")}</Chip>
                  <span className="font-mono text-[11px] text-GOLD" style={{ color: GOLD }}>${(c.value_usd/1_000_000).toFixed(0)}M</span>
                </div>
                <div className="mt-1"><Bar value={c.completion_pct} accent={GOLD} /></div>
                <div className="mt-1 text-[9.5px] text-[#7C93A5]">
                  {c.projects} projects · {c.jobs.toLocaleString()} jobs · {c.completion_pct}% complete
                </div>
              </div>
            ))}
          </div>
        </Card>
      </div>

      {/* Delivery Centre + Cabinet Action Tracker */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
        <Card title="Government Delivery Centre™" accent={PURPLE} testId="spp-delivery"
              right={<Chip color={PURPLE}>{delivery.length} programmes</Chip>}>
          <div className="space-y-1 max-h-[300px] overflow-auto">
            {delivery.slice(0, 20).map((d) => (
              <Link key={d.programme_id} to="/app/cabinet-execution"
                    className="rounded-sm border px-2 py-1.5 hover:brightness-125"
                    style={{ borderColor: PURPLE + "30", background: "rgba(7,27,43,0.5)" }}>
                <div className="flex items-center gap-2 mb-0.5">
                  <StatusDot v={d.delivery_pct} />
                  <div className="text-[11.5px] text-[#F6F8FA] flex-1 truncate">{d.programme}</div>
                  <Chip color={d.current_risk === "high" ? RED : d.current_risk === "medium" ? AMBER : GREEN} filled>{d.current_risk}</Chip>
                </div>
                <Bar value={d.delivery_pct} accent={PURPLE} />
                <div className="mt-1 text-[9.5px] text-[#7C93A5]">
                  {d.minister} · {d.province} · Budget ${(d.budget_usd/1_000_000).toFixed(1)}M · {d.delivery_pct}%
                </div>
              </Link>
            ))}
            {delivery.length === 0 && <div className="text-[10.5px] text-[#7C93A5] italic">No linked programmes yet.</div>}
          </div>
        </Card>

        <Card title="Cabinet Action Tracker™ · signed & audited" accent={GOLD} testId="spp-tracker"
              right={<Link to="/app/cabinet-execution" className="text-[10.5px] font-mono text-[#7C93A5] hover:text-[#F6F8FA]">Execution Engine →</Link>}>
          <div className="space-y-1 max-h-[300px] overflow-auto">
            {tracker.slice(0, 18).map((t) => (
              <Link key={t.id} to="/app/cabinet-execution"
                    className="rounded-sm border px-2 py-1.5 flex items-start gap-2 hover:brightness-125"
                    style={{ borderColor: GOLD + "30", background: "rgba(7,27,43,0.5)" }}>
                <ClipboardCheck size={11} style={{ color: GOLD }} className="mt-0.5" />
                <div className="flex-1 min-w-0">
                  <div className="text-[11.5px] text-[#F6F8FA] truncate">{t.title}</div>
                  <div className="text-[9.5px] text-[#7C93A5]">{t.owner_ministry} · {t.current_status} · audit {t.audit_trail_len}</div>
                </div>
                <Chip color={t.priority === "critical" ? RED : t.priority === "high" ? AMBER : BLUE} filled>{t.priority}</Chip>
              </Link>
            ))}
          </div>
        </Card>
      </div>

      {/* Benefits + Governance Index + Twin overlays + Partners */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
        <Card title="National Benefits Realisation™" accent={GREEN} testId="spp-benefits">
          {benefits ? (
            <div className="grid grid-cols-2 gap-1.5">
              {[
                ["Revenue Generated",     "$" + Number(benefits.revenue_generated_usd || 0).toLocaleString()],
                ["Investment Unlocked",   "$" + Number(benefits.investment_unlocked_usd).toLocaleString()],
                ["Employment Created",    Number(benefits.employment_created).toLocaleString()],
                ["Licences Processed",    Number(benefits.licences_processed).toLocaleString()],
                ["Permit Turnaround Δ",   benefits.permit_turnaround_improvement_pct + "%"],
                ["Land Disputes Resolved",Number(benefits.land_disputes_resolved).toLocaleString()],
                ["Env Compliance",        benefits.environmental_compliance_pct + "%"],
                ["Paper Eliminated",      Number(benefits.paper_eliminated_pages).toLocaleString() + " pages"],
                ["GDP Contribution",      benefits.gdp_contribution_pct + "%"],
                ["Economic Contribution", "$" + Number(benefits.economic_contribution_usd).toLocaleString()],
              ].map(([l, v]) => (
                <div key={l} className="rounded-sm border px-2 py-1"
                     style={{ borderColor: GREEN + "30", background: "rgba(7,27,43,0.5)" }}>
                  <div className="font-mono uppercase text-[9px] tracking-[0.14em] text-[#7C93A5] truncate">{l}</div>
                  <div className="text-[12.5px] font-bold" style={{ color: GREEN }}>{v}</div>
                </div>
              ))}
            </div>
          ) : <div className="text-[11px] text-[#7C93A5]">Loading…</div>}
        </Card>

        <Card title="National Governance Index™" accent={TEAL} testId="spp-gov-index">
          {gov ? (
            <div className="space-y-1.5">
              {gov.dimensions.slice(0, 10).map((d) => (
                <div key={d.key}>
                  <div className="flex items-center gap-2 mb-0.5">
                    <div className="text-[11px] text-[#F6F8FA] flex-1 truncate">{d.label}</div>
                    <span className="font-mono text-[11px] text-[#F6F8FA]">{d.score}</span>
                  </div>
                  <Bar value={d.score} accent={TEAL} />
                </div>
              ))}
              <div className="pt-1 text-[10.5px] text-[#7C93A5] font-mono uppercase tracking-[0.14em]">
                Overall {gov.overall} · benchmark alignment across {gov.benchmarks?.length || 0} frameworks
              </div>
            </div>
          ) : <div className="text-[11px] text-[#7C93A5]">Loading…</div>}
        </Card>
      </div>

      {/* Digital Twin overlays + Development Partners + Strategic Opportunities */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-3">
        <Card title="Executive Digital Twin™ overlays" accent={BLUE} testId="spp-twin"
              right={<Link to="/app/twin" className="text-[10.5px] font-mono text-[#7C93A5] hover:text-[#F6F8FA]">Explore Twin →</Link>}>
          <div className="grid grid-cols-2 gap-1 max-h-[300px] overflow-auto">
            {twin.map((o) => (
              <Link key={o.key} to="/app/twin"
                    className="rounded-sm border px-2 py-1 flex items-center gap-1.5 hover:brightness-125"
                    style={{ borderColor: BLUE + "30", background: "rgba(7,27,43,0.5)" }}>
                <Boxes size={10} style={{ color: BLUE }} />
                <div className="flex-1 text-[10.5px] text-[#F6F8FA] truncate">{o.label}</div>
                <span className="font-mono text-[9.5px] text-[#7C93A5]">{o.coverage_pct}%</span>
              </Link>
            ))}
          </div>
        </Card>

        <Card title="Development Partner Performance" accent={PURPLE} testId="spp-partners">
          <div className="space-y-1 max-h-[300px] overflow-auto">
            {partners.map((p) => (
              <div key={p.key} className="rounded-sm border px-2 py-1.5"
                   style={{ borderColor: PURPLE + "30", background: "rgba(7,27,43,0.5)" }}>
                <div className="flex items-center gap-2">
                  <Handshake size={11} style={{ color: PURPLE }} />
                  <div className="text-[11px] text-[#F6F8FA] flex-1">{p.label}</div>
                  <Chip color={p.delivery === "on_track" ? GREEN : p.delivery === "at_risk" ? AMBER : RED} filled>{p.delivery.replace("_"," ")}</Chip>
                </div>
                <div className="mt-1 text-[9.5px] text-[#7C93A5]">
                  {p.projects} projects · ${(p.funding_usd/1_000_000).toFixed(0)}M · {p.implementation_pct}%
                </div>
                <Bar value={p.implementation_pct} accent={PURPLE} />
              </div>
            ))}
          </div>
        </Card>

        <Card title="Strategic Opportunities™" accent={GREEN} testId="spp-opps"
              right={<Link to="/app/gsros" className="text-[10.5px] font-mono text-[#7C93A5] hover:text-[#F6F8FA]">GSROS →</Link>}>
          <div className="space-y-1 max-h-[300px] overflow-auto">
            {opps.map((o) => (
              <Link key={o.category} to={o.linked_module || "/app/gsros"}
                    className="rounded-sm border px-2 py-1.5 hover:brightness-125"
                    style={{ borderColor: GREEN + "30", background: "rgba(7,27,43,0.5)" }}>
                <div className="flex items-center gap-2">
                  <Star size={11} style={{ color: GREEN }} />
                  <div className="text-[11px] text-[#F6F8FA] flex-1">{o.category}</div>
                  <span className="font-mono text-[11px]" style={{ color: GREEN }}>${(o.value_usd/1_000_000).toFixed(0)}M</span>
                </div>
                <div className="mt-0.5 text-[9.5px] text-[#7C93A5]">Confidence {(o.confidence*100).toFixed(0)}% · {o.priority}</div>
              </Link>
            ))}
          </div>
        </Card>
      </div>

      <div className="text-[10.5px] text-[#7C93A5] font-mono uppercase tracking-[0.18em] pt-1">
        Sovereign Performance Platform™ · Phase XVIII orchestration · consumes existing services · zero regression · every card deep-links
      </div>
    </div>
  );
};

export default SovereignPerformancePlatform;
